// Role-aware dispatcher rendered at the SPA's `/` route.
//
// Until issue #330 the root path unconditionally sent every user to
// `/admin/dashboard`, which produced a clunky bounce for field_staff:
// they would land on the admin URL, RequireAdmin would render
// UnauthorizedPage, and they had to click "Back to capture home" to
// reach the surface they actually have access to. Now the dispatch is
// driven by the resolved session so each role lands directly on its
// home surface:
//
//   * admin (pure)        → /admin/dashboard
//   * admin + field group → /capture   (dual-group user — resolves to
//                           the admin role since admin wins, but their
//                           on-site job is capture, so the front door
//                           sends them to capture; they keep full admin
//                           access by navigating to /admin. An installed
//                           PWA has no address bar, so this redirect is
//                           the only way such a user reaches capture.)
//   * field_staff         → /capture
//
// The downstream guards (RequireAdmin / RequireAuth) are unchanged and
// still enforce role access; this component only chooses the
// post-login destination from the front door.
//
// Branching rules mirror RequireAuth/RequireAdmin so all three guards
// stay behaviourally consistent on loading and error states. The one
// deliberate difference is the `unauthenticated` branch: at `/` we
// route to the SPA's `/sign-in` page so users see the LoginPage (with
// its Microsoft + decorative-credentials forms) rather than being
// kicked straight into the backend's Entra round-trip. The protected
// surfaces still drop unauthenticated callers directly at
// `/auth/login` because the user already had a deep destination in
// mind that needs preserving via redirect_to.
import { Navigate } from 'react-router'

import { BrandedSplash } from '../ui/branded-splash'

import { useAuth } from './auth-provider'

export function RoleHomeRedirect() {
  const { status, user, error } = useAuth()

  if (status === 'loading') {
    return <BrandedSplash message="Loading session…" />
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/sign-in" replace />
  }

  if (status === 'error') {
    return (
      <div
        role="alert"
        className="flex min-h-screen items-center justify-center p-6 text-sm text-red-700"
      >
        <div className="max-w-md space-y-2 text-center">
          <h1 className="text-lg font-semibold">Could not verify session</h1>
          <p>{error?.message ?? 'Unknown error contacting the server.'}</p>
        </div>
      </div>
    )
  }

  // Only a *pure* admin (admin role AND not a field-group member) lands
  // on the admin dashboard. A dual-group user resolves to the admin
  // role (admin wins) but carries isFieldStaffMember, so they fall to
  // the /capture branch alongside field staff — their on-site job is
  // capture and an installed PWA has no address bar to navigate with.
  // A missing flag (a session minted before this shipped) is treated as
  // pure-admin, preserving the prior behaviour until the user re-auths.
  const isPureAdmin =
    user?.role === 'admin' && user.isFieldStaffMember !== true
  const destination = isPureAdmin ? '/admin/dashboard' : '/capture'
  return <Navigate to={destination} replace />
}
